/**
 * Service for the ERP-Saldo Sammenligning module.
 * Calls the BalanceComparisonController and ErpBalanceImportController.
 */

import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import {
  BalanceComparisonFilter,
  BalanceComparisonResult,
  BalanceComparisonSummary,
  ErpBalanceImportRequest,
  ErpBalanceImportResponse,
} from '../types/balanceComparison';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class BalanceComparisonService {
  private compareUrl = `${API_BASE_URL}/balance-comparison`;
  private importUrl = `${API_BASE_URL}/erp-balance-import`;

  /**
   * Fetch the paginated balance-comparison list according to filters.
   */
  async getComparison(filter: BalanceComparisonFilter): Promise<BalanceComparisonResult> {
    try {
      const params = new URLSearchParams();
      if (filter.warehouseID) params.append('warehouseID', filter.warehouseID);
      if (filter.itemNoSearch) params.append('itemNoSearch', filter.itemNoSearch);
      if (filter.description) params.append('description', filter.description);
      if (filter.onlyOutOfSync !== undefined) params.append('onlyOutOfSync', String(filter.onlyOutOfSync));
      if (filter.onlyCountable !== undefined) params.append('onlyCountable', String(filter.onlyCountable));
      if (filter.movementInsights?.length) {
        filter.movementInsights.forEach((mi) => params.append('movementInsights', mi));
      }
      if (filter.presence) params.append('presence', filter.presence);
      if (filter.page !== undefined) params.append('page', String(filter.page));
      if (filter.pageSize !== undefined) params.append('pageSize', String(filter.pageSize));
      if (filter.sortBy) params.append('sortBy', filter.sortBy);
      if (filter.sortDirection) params.append('sortDirection', filter.sortDirection);
      if (filter.includeEmptyBins !== undefined) params.append('includeEmptyBins', String(filter.includeEmptyBins));
      if (filter.hideMovedSinceSnapshot !== undefined) params.append('hideMovedSinceSnapshot', String(filter.hideMovedSinceSnapshot));

      const response = await AuthService.authenticatedGet(`${this.compareUrl}?${params.toString()}`);

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(extractApiError(error, response, 'Kunne ikke hente saldosammenligning'));
      }

      const data: BalanceComparisonResult = await response.json();

      // Backend returns rows without an `id` field (id is computed).
      // Add a stable composite id and ensure locations[] exists.
      data.rows = (data.rows || []).map((row) => ({
        ...row,
        id: `${row.warehouseID}|${row.itemNo}`,
        locations: row.locations ?? [],
      }));

      return data;
    } catch (error) {
      console.error('Error fetching balance comparison:', error);
      throw error;
    }
  }

  /**
   * Fetch the small summary used by the manager dashboard card.
   */
  async getSummary(warehouseID?: string): Promise<BalanceComparisonSummary> {
    try {
      const params = new URLSearchParams();
      if (warehouseID) params.append('warehouseID', warehouseID);

      const url = params.toString()
        ? `${this.compareUrl}/summary?${params.toString()}`
        : `${this.compareUrl}/summary`;

      const response = await AuthService.authenticatedGet(url);

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(extractApiError(error, response, 'Kunne ikke hente sammendrag av saldosammenligning'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching balance comparison summary:', error);
      throw error;
    }
  }

  /**
   * Submit an ERP balance snapshot. Normally called by eSENTRAL — exposed
   * for manual testing / debugging from the UI.
   */
  async importSnapshot(request: ErpBalanceImportRequest): Promise<ErpBalanceImportResponse> {
    try {
      const response = await AuthService.authenticatedPost(this.importUrl, request);

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(extractApiError(error, response, 'Import failed'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error importing ERP balance snapshot:', error);
      throw error;
    }
  }
}

export default new BalanceComparisonService();
